import { markCvOnce } from "./cvPerfMarks";

const DEFAULT_MEDIA_CONSTRAINTS = {
  audio: { sampleRate: 16000, sampleSize: 16, channelCount: 1 },
  video: {
    width: { min: 480, max: 640 },
    height: { min: 480, max: 640 },
    frameRate: { min: 15, max: 30 },
    facingMode: "user",
  },
};

export class CvWebRtcClient {
  constructor({ iceServers = [], mediaConstraints = DEFAULT_MEDIA_CONSTRAINTS, onConnectionStateChange } = {}) {
  this.iceServers = iceServers;
  this.mediaConstraints = mediaConstraints;
  this.onConnectionStateChange = onConnectionStateChange;
  this.id = Math.random().toString(36).slice(2, 7); // метка сессии для логов
  this.pc = null;
  this.stream = null;
  }

  get peerConnection() { return this.pc; }

    async start(options = {}) {
    if (this.pc) throw new Error("CvWebRtcClient: start() уже вызывался");
    try {
      this.stream = options?.warmStream
        ? options.warmStream
        : await navigator.mediaDevices.getUserMedia(this.mediaConstraints);
    } catch (err) {
      throw new Error(`Доступ к камере: ${err.message}`);
    }
    markCvOnce("gum:done");
  this.pc = new RTCPeerConnection({ iceServers: this.iceServers });

    const pc = this.pc;
    const tag = `[CV ${this.id}]`;
    pc.addEventListener("connectionstatechange", () => {
      console.log(tag, "connectionState →", pc.connectionState, new Date().toISOString());
      if (this.pc === pc && this.onConnectionStateChange) this.onConnectionStateChange(pc.connectionState);
    });
    pc.addEventListener("iceconnectionstatechange", () => console.log(tag, "iceConnectionState →", pc.iceConnectionState));
    this.stream.getTracks().forEach((t) => {
      t.addEventListener("ended", () => console.warn(tag, "track ended:", t.kind));
      t.addEventListener("mute", () => console.warn(tag, "track muted:", t.kind));
    });

    this.stream.getTracks().forEach((track) => this.pc.addTrack(track, this.stream));
    return this.stream;
  }

  async createOffer() {
    if (!this.pc) throw new Error("CvWebRtcClient: сначала вызови start()");
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    await this._waitForIceGatheringComplete(this.pc);
    return this._removeExtraCodecs(this.pc.localDescription);
  }

  createDataChannel(label, options) {
    if (!this.pc) throw new Error("CvWebRtcClient: сначала вызови start()");
    return this.pc.createDataChannel(label, options);
  }

  async setRemoteAnswer(answer) {
    if (!this.pc) throw new Error("CvWebRtcClient: сначала вызови start()");
    console.log("CvWebRtcClient: setRemoteAnswer starting...");
    await this.pc.setRemoteDescription(answer);
    console.log("CvWebRtcClient: setRemoteAnswer done!");
  }

    close() {
    const pc = this.pc;
    this.pc = null; // гасит колбэки состояния: this.pc !== pc
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    if (pc) pc.close();
    this.stream = null;
  }

  _waitForIceGatheringComplete(pc) {
    if (pc.iceGatheringState === "complete") return Promise.resolve();
    return new Promise((resolve) => {
      const check = () => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", check);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", check);
      // Таймаут 3 секунды, чтобы не висеть вечно
      setTimeout(() => {
        if (pc.iceGatheringState !== "complete") {
          console.warn("ICE gathering timeout, proceeding anyway");
          pc.removeEventListener("icegatheringstatechange", check);
          resolve();
        }
      }, 3000);
    });
  }

  _sdpFilterCodec(kind, codec, realSdp) {
    const lines = realSdp.split("\n");
    if (!lines.some((l) => l.startsWith(`m=${kind} `))) return realSdp;
    const allowed = []; const result = [];
    lines.forEach((l) => {
      if (l.startsWith("a=rtpmap:") && l.includes(codec)) {
        const match = l.match(/a=rtpmap:(\d+)/);
        if (match) allowed.push(match[1]);
      }
    });
    lines.forEach((l) => {
      if (l.startsWith(`m=${kind}`)) {
        const parts = l.split(" "); const head = parts.slice(0, 3);
        parts.slice(3).forEach((pt) => { if (allowed.includes(pt)) head.push(pt); });
        l = head.join(" ");
      }
      result.push(l);
    });
    return result.join("\n");
  }

  _removeExtraCodecs(offer) {
    let sdp = offer.sdp;
    try { sdp = this._sdpFilterCodec("video", "VP8", offer.sdp); } catch (e) {}
    try { sdp = this._sdpFilterCodec("audio", "opus", sdp); } catch (e) {}
    return new RTCSessionDescription({ type: offer.type, sdp: sdp });
  }
}

if (typeof window !== "undefined") {
  window.EzSpeechCv = window.EzSpeechCv || {};
  window.EzSpeechCv.CvWebRtcClient = CvWebRtcClient;
}